import { cn } from '@/lib/utils';
import { Activity, Gauge, Timer, TrendingUp } from 'lucide-react';
import { MetricInfoButton } from '@/components/MetricInfoButton';
import type { TrainingLoad } from '@/types';

interface InsightsVitalsSidebarProps {
  data: TrainingLoad[];
  overview60d?: {
    today: { ctl: number; atl: number; tsb: number };
    metrics: {
      ctl?: [string, number][];
      atl?: [string, number][];
      tsb?: [string, number][];
    };
  } | null;
  className?: string;
}

/** HUD glass card wrapper */
function VitalCard({ 
  label, value, unit, subtext, status, icon: Icon, metricKey 
}: { 
  label: string; 
  value: string; 
  unit?: string; 
  subtext?: string; 
  status: 'optimal' | 'low'; 
  icon: React.ElementType;
  metricKey?: string;
}) {
  const statusColor = status === 'optimal' 
    ? 'hsl(170, 80%, 45%)' // teal
    : 'hsl(35, 85%, 55%)'; // amber

  return (
    <div 
      className="p-4 rounded-lg border border-white/[0.08] bg-[rgba(12,16,24,0.85)] backdrop-blur-[20px]"
      style={{
        boxShadow: `inset 0 1px 0 ${statusColor}10, 0 0 20px ${statusColor}06`,
      }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-3.5 w-3.5" style={{ color: statusColor }} strokeWidth={1.5} />
        <span className="text-[10px] font-mono tracking-[0.15em] uppercase text-muted-foreground">
          {label}
        </span>
        {metricKey && <MetricInfoButton metricKey={metricKey} className="ml-auto" />}
      </div>
      <div className="flex items-baseline gap-1.5">
        <span 
          className="text-2xl font-mono font-light tabular-nums"
          style={{ color: statusColor }}
        >
          {value}
        </span>
        {unit && (
          <span className="text-xs font-mono text-muted-foreground">{unit}</span>
        )}
      </div>
      {subtext && (
        <p className="text-[10px] font-mono text-muted-foreground mt-2 tracking-wide">
          {subtext}
        </p>
      )}
    </div>
  );
}

export function InsightsVitalsSidebar({ data, overview60d, className }: InsightsVitalsSidebarProps) {
  const today = overview60d?.today;
  const ctl = today?.ctl ?? 0;
  const atl = today?.atl ?? 0;
  const tsb = today?.tsb ?? 0;

  // Weekly Training Load = sum of last 7 days TSS (dailyLoad)
  const last7 = data.slice(-7);
  const weeklyLoad = last7.reduce((sum, d) => sum + (d.dailyLoad || 0), 0);

  // Efficiency: CTL / ATL ratio (higher = more fitness per fatigue)
  const efficiency = atl > 0 ? (ctl / atl * 100).toFixed(0) : '—';
  const efficiencyStatus: 'optimal' | 'low' = atl > 0 && ctl / atl >= 0.8 ? 'optimal' : 'low';

  // Recovery baseline from TSB
  const recoveryStatus: 'optimal' | 'low' = tsb > -10 ? 'optimal' : 'low';

  return (
    <div className={cn('space-y-3', className)}>
      {/* Header */}
      <div className="flex items-center gap-2 mb-1">
        <div className="w-1 h-1 rounded-full bg-[hsl(170,80%,45%)]" />
        <span className="text-[9px] font-mono tracking-[0.2em] uppercase text-muted-foreground">
          SYSTEM VITALS
        </span>
      </div>

      <VitalCard
        label="Weekly Training Load"
        value={weeklyLoad > 0 ? Math.round(weeklyLoad).toFixed(0) : '—'}
        unit=""
        subtext="Last 7 days TSS"
        status={weeklyLoad > 0 ? 'optimal' : 'low'}
        icon={Activity}
        metricKey="ctl"
      />

      <VitalCard
        label="Load Balance"
        value={efficiency}
        unit="%"
        subtext="Ratio of capacity to fatigue"
        status={efficiencyStatus}
        icon={Gauge}
        metricKey="loadBalance"
      />

      <VitalCard
        label="Freshness Index"
        value={tsb > 0 ? `+${tsb.toFixed(0)}` : tsb.toFixed(0)}
        unit=""
        subtext={tsb > 5 ? 'Fresh — ready for load' : tsb > -10 ? 'Balanced recovery' : 'Fatigue accumulating'}
        status={recoveryStatus}
        icon={Timer}
        metricKey="tsb"
      />

      <VitalCard
        label="Aerobic Base"
        value={ctl.toFixed(0)}
        unit=""
        subtext={`Recent Load: ${atl.toFixed(0)}`}
        status={ctl >= atl * 0.8 ? 'optimal' : 'low'}
        icon={TrendingUp}
        metricKey="ctl"
      />
    </div>
  );
}
